/**
 * Search tab — shared client-side kind metadata, condition describers, and
 * definition helpers. The server catalog (backend crmSearch/catalog.js) is the
 * validation authority; this mirrors just enough vocabulary to build editors
 * and readable summaries. Keep the kind list in lock-step with the catalog.
 */
import type { SearchCondition, SearchDefinition, SearchStepDef, StudyOption, TeamMember, CrmForm } from '../../types';
import { studyInternalLabel } from '../../types';
import { INCOMING_ELIGIBILITY_OPTIONS, LIFECYCLES, MILESTONE_LABELS, prettySlug } from '../../lib';

export const KIND_CATEGORIES = [
  { key: 'pipeline', label: 'Pipeline & attribution' },
  { key: 'medical', label: 'Medical profile' },
  { key: 'forms', label: 'Forms & answers' },
  { key: 'eligibility', label: 'Eligibility & screening' },
  { key: 'engagement', label: 'Engagement & records' },
] as const;

export interface KindMeta {
  kind: string;
  label: string;
  category: (typeof KIND_CATEGORIES)[number]['key'];
  hint: string;
}

/** Palette entries (P1 + P2 catalog). Order within a category = display order. */
export const KINDS: KindMeta[] = [
  { kind: 'stage', label: 'Stage', category: 'pipeline', hint: 'Current pipeline stage' },
  { kind: 'stage_ever', label: 'Stage ever reached', category: 'pipeline', hint: 'History: ever scheduled, lost after qualifying…' },
  { kind: 'study', label: 'Study', category: 'pipeline', hint: 'Current study, or ever submitted to one' },
  { kind: 'no_study', label: 'No current study', category: 'pipeline', hint: 'Leads not considering any study — the cross-sell pool' },
  { kind: 'owner', label: 'Owner', category: 'pipeline', hint: 'Assigned recruiter (or unassigned)' },
  { kind: 'incoming_eligibility', label: 'Incoming eligibility', category: 'pipeline', hint: 'Best landing-form verdict' },
  { kind: 'milestone', label: 'Milestone', category: 'pipeline', hint: 'The derived sub-line milestone' },
  { kind: 'lead_source', label: 'Lead source', category: 'pipeline', hint: 'fb / ig / manual / import…' },
  { kind: 'utm', label: 'UTM attribution', category: 'pipeline', hint: 'Campaign / source / medium from native lead sessions' },
  { kind: 'disposition', label: 'Disposition', category: 'pipeline', hint: 'Closed out — with which reason, on which study' },
  { kind: 'tag', label: 'Tag', category: 'pipeline', hint: 'Contact tags (e.g. crio:completed)' },
  { kind: 'interest', label: 'Future-study list', category: 'pipeline', hint: 'Kept for a study type we regularly get (gout, migraine…)' },
  { kind: 'created', label: 'Created date', category: 'pipeline', hint: 'When the lead first arrived' },
  { kind: 'activity', label: 'Activity recency', category: 'pipeline', hint: 'Contacted / heard from within N days — or not' },
  { kind: 'contactable', label: 'Contactable', category: 'pipeline', hint: 'Has a phone (not opted out) / a non-suppressed email' },
  { kind: 'snoozed', label: 'Snoozed', category: 'pipeline', hint: 'Currently snoozed, or not' },
  { kind: 'archived', label: 'Archived', category: 'pipeline', hint: 'Only meaningful when the base includes archived leads' },
  { kind: 'condition', label: 'Medical condition', category: 'medical', hint: 'Diagnosis on the profile — form, AI, or recruiter entered' },
  { kind: 'medication', label: 'Medication', category: 'medical', hint: 'Medication on the profile' },
  { kind: 'lab', label: 'Lab value', category: 'medical', hint: 'Numeric compare on a lab (A1C ≥ 7, within 12 months…)' },
  { kind: 'vital', label: 'Vital', category: 'medical', hint: 'BMI, weight, height, BP, heart rate' },
  { kind: 'demographic', label: 'Demographic', category: 'medical', hint: 'Age or sex — where a form asked or extraction found it' },
  { kind: 'form_submitted', label: 'Form submitted', category: 'forms', hint: 'Completed (or started) a specific form' },
  { kind: 'answer', label: 'Answer', category: 'forms', hint: 'Answered a question a specific way — anywhere, or on one form' },
  { kind: 'answered', label: 'Answered question', category: 'forms', hint: 'Answered a question at all (any value)' },
  { kind: 'study_eligibility', label: 'Study eligibility', category: 'eligibility', hint: 'Prescreen verdict for one study (eligible / not / withdrew)' },
  { kind: 'ai_review', label: 'AI eligibility review', category: 'eligibility', hint: 'Outcome of the AI records review' },
  { kind: 'dq', label: 'Disqualification', category: 'eligibility', hint: 'Screened out — by category, study, or when' },
  { kind: 'communication', label: 'Communication volume', category: 'engagement', hint: 'N+ calls / texts / emails within a window' },
  { kind: 'has_records', label: 'Has records on file', category: 'engagement', hint: 'Medical records / summaries uploaded or faxed in' },
];

export const kindMeta = (kind: string): KindMeta | undefined => KINDS.find((k) => k.kind === kind);

/** Fresh condition with sensible defaults for a just-picked kind. */
export function defaultConditionFor(kind: string): SearchCondition {
  switch (kind) {
    case 'stage': return { kind, statuses: [] };
    case 'stage_ever': return { kind, statuses: [] };
    case 'study': return { kind, slugs: [], scope: 'current' };
    case 'owner': return { kind, user_ids: [], include_unassigned: false };
    case 'incoming_eligibility': return { kind, values: [] };
    case 'milestone': return { kind, keys: [] };
    case 'lead_source': return { kind, sources: [] };
    case 'utm': return { kind, field: 'campaign', values: [] };
    case 'disposition': return { kind, to_statuses: [] };
    case 'tag': return { kind, tags: [] };
    case 'interest': return { kind, lists: [] };
    case 'created': return { kind, from: '', to: '' };
    case 'activity': return { kind, field: 'any', op: 'not_within', days: 30 };
    case 'contactable': return { kind, channel: 'sms' };
    case 'snoozed': return { kind, state: 'snoozed' };
    case 'archived': return { kind, state: 'archived' };
    case 'condition': return { kind, tag: '', name: '', statuses: ['active'] };
    case 'medication': return { kind, tag: '', name: '', statuses: ['active'] };
    case 'lab': return { kind, test_name: '', op: 'gte', value: '' };
    case 'vital': return { kind, preset: 'bmi', op: 'gte', value: '' };
    case 'demographic': return { kind, key: 'age', op: 'between', value: '', value2: '' };
    case 'form_submitted': return { kind, form_id: '', status: 'completed' };
    case 'answer': return { kind, question: '', op: 'eq', value: '' };
    case 'answered': return { kind, question: '' };
    case 'study_eligibility': return { kind, study_id: '', verdicts: [] };
    case 'ai_review': return { kind, results: [], latest_only: true };
    case 'dq': return { kind, categories: [] };
    case 'communication': return { kind, channels: ['call'], direction: 'any', within_days: 30, min_count: 1 };
    case 'has_records': return { kind, doc_types: ['medical_record'] };
    default: return { kind };
  }
}

export const OP_LABELS: Record<string, string> = {
  eq: '=', neq: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'between',
  contains: 'contains', includes: 'includes', not_includes: "doesn't include", answered: 'answered',
};

export const NUMERIC_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'];

export const VITAL_PRESET_OPTIONS = [
  { value: 'bmi', label: 'BMI' },
  { value: 'weight', label: 'Weight' },
  { value: 'height', label: 'Height' },
  { value: 'bp_systolic', label: 'BP systolic' },
  { value: 'bp_diastolic', label: 'BP diastolic' },
  { value: 'heart_rate', label: 'Heart rate' },
];

export const WARNING_LABELS: Record<string, string> = {
  low_coverage: 'Low data coverage — most leads have no data for this; consider "keep no-data" or tag more forms',
  no_matches: 'Matches nobody entering this step',
  matched_all: 'Matches EVERYONE entering this step — probable misconfiguration',
  milestone_truncated: 'Milestone candidate cap hit — counts may be partial',
  unknown_kind: 'A criterion type from an older version — matches nothing',
  mixed_units: 'This test has mixed units across results — values are compared raw, never converted',
  numeric_op_on_text: 'Numeric comparison on a non-numeric question — it will match nothing',
  contains_on_choice: '"contains" on a choice question is unreliable — compare the option value with = instead',
  drop_unknown_heavy: 'This exclude step\'s "no data: excluded too" wipes over half the entering leads',
};

/** The forward spine in ordinal order (stage_ever's min_ordinal picker). */
export const SPINE_STAGES: { ordinal: number; value: string }[] = [
  { ordinal: 1, value: 'new' },
  { ordinal: 2, value: 'contacting' },
  { ordinal: 3, value: 'qualified_pending_records' },
  { ordinal: 4, value: 'ready_to_schedule' },
  { ordinal: 5, value: 'scheduled' },
  { ordinal: 6, value: 'screened' },
  { ordinal: 7, value: 'randomized' },
];

export const DISPOSITION_STATUS_OPTIONS = [
  { value: 'not_interested', label: 'Not interested' },
  { value: 'not_eligible', label: 'Not eligible' },
  { value: 'screen_failed', label: 'Screen failed' },
  { value: 'unreachable', label: 'Unreachable' },
];

export const DQ_CATEGORY_OPTIONS = ['age', 'demographics', 'diagnosis', 'medication', 'labs', 'procedure',
  'lifestyle', 'logistics', 'withdrew_interest', 'consent', 'washout', 'other']
  .map((v) => ({ value: v, label: v.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) }));

export const STUDY_ELIGIBILITY_VERDICT_OPTIONS = [
  { value: 'eligible', label: 'Eligible' },
  { value: 'screening', label: 'Prescreening' },
  { value: 'not_eligible', label: 'Not eligible' },
  { value: 'withdrew', label: 'Withdrew' },
];

export const AI_REVIEW_RESULT_OPTIONS = [
  { value: 'eligible', label: 'Eligible' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'review_required', label: 'Review required' },
  { value: 'incomplete', label: 'Incomplete' },
];

export const UTM_FIELD_OPTIONS = ['source', 'medium', 'campaign', 'content', 'term']
  .map((v) => ({ value: v, label: `utm_${v}` }));

export const COMM_CHANNEL_OPTIONS = [
  { value: 'call', label: 'Calls' },
  { value: 'sms', label: 'Texts' },
  { value: 'email', label: 'Emails' },
];

export interface DescribeCtx {
  studies?: StudyOption[];
  team?: TeamMember[];
  forms?: CrmForm[];
}

const list = (vals: string[] | undefined, map: (v: string) => string, max = 3): string => {
  const v = vals || [];
  if (!v.length) return '…';
  const shown = v.slice(0, max).map(map).join(', ');
  return v.length > max ? `${shown} +${v.length - max}` : shown;
};

/** One-line human summary of a condition (step rows, funnel labels, chips). */
export function describeCondition(c: SearchCondition, ctx: DescribeCtx = {}): string {
  const stageLabel = (v: string) => LIFECYCLES.find((l) => l.value === v)?.label || v;
  const studyLabel = (slug: string) => {
    const s = (ctx.studies || []).find((x) => x.slug === slug);
    return s ? studyInternalLabel(s) : prettySlug(slug);
  };
  const ownerLabel = (id: string) => (ctx.team || []).find((m) => m.id === id)?.name || 'owner';
  const formLabel = (id: string) => (ctx.forms || []).find((f) => f.id === id)?.title || 'a form';
  const num = () => (c.op === 'between' ? `between ${c.value ?? '…'} and ${c.value2 ?? '…'}` : `${OP_LABELS[c.op || ''] || c.op} ${c.value ?? '…'}`);

  const studyByIdLabel = (id: string) => {
    const s = (ctx.studies || []).find((x) => x.id === id);
    return s ? studyInternalLabel(s) : 'a study';
  };

  switch (c.kind) {
    case 'stage': return `Stage: ${list(c.statuses, stageLabel)}`;
    case 'stage_ever': {
      if (c.min_ordinal !== undefined && c.min_ordinal !== null && String(c.min_ordinal) !== '') {
        const spine = SPINE_STAGES.find((s) => s.ordinal === Number(c.min_ordinal));
        return `Ever reached: ${spine ? stageLabel(spine.value) : `spine ${c.min_ordinal}`}+`;
      }
      return `Ever in: ${list(c.statuses, stageLabel)}`;
    }
    case 'study': return `${c.scope === 'ever' ? 'Ever submitted to' : 'Study'}: ${list(c.slugs, studyLabel)}`;
    case 'no_study': return 'No current study';
    case 'owner': {
      const parts = (c.user_ids || []).map(ownerLabel);
      if (c.include_unassigned) parts.push('unassigned');
      return `Owner: ${parts.length ? parts.slice(0, 3).join(', ') + (parts.length > 3 ? ` +${parts.length - 3}` : '') : '…'}`;
    }
    case 'incoming_eligibility':
      return `Incoming eligibility: ${list(c.values, (v) => INCOMING_ELIGIBILITY_OPTIONS.find((o) => o.value === v)?.label || v)}`;
    case 'milestone':
      return `Milestone: ${list(c.keys, (v) => (MILESTONE_LABELS as Record<string, string>)[v] || v)}`;
    case 'lead_source': return `Source: ${list(c.sources, prettySlug)}`;
    case 'utm': return `utm_${c.field || '…'}: ${list(c.values, (v) => v)}`;
    case 'disposition': {
      const what = (c.to_statuses || []).length
        ? list(c.to_statuses, (v) => DISPOSITION_STATUS_OPTIONS.find((o) => o.value === v)?.label || v)
        : 'any status';
      return `Dispositioned: ${what}${(c.reason_codes || []).length ? ` (${list(c.reason_codes, (v) => v)})` : ''}`;
    }
    case 'tag': return `Tagged: ${list(c.tags, (v) => v)}`;
    case 'interest': return `Future-study list: ${list(c.lists, prettySlug)}`;
    case 'created': {
      const from = c.from ? String(c.from).slice(0, 10) : null;
      const to = c.to ? String(c.to).slice(0, 10) : null;
      return `Created ${from ? `from ${from}` : ''}${from && to ? ' ' : ''}${to ? `to ${to}` : ''}`.trim();
    }
    case 'activity': {
      const f = c.field === 'inbound' ? 'Heard from' : c.field === 'outbound' ? 'Contacted' : 'Any activity';
      return `${f} ${c.op === 'within' ? 'within' : 'NOT within'} ${c.days ?? '…'} days`;
    }
    case 'contactable': return `Contactable by ${c.channel === 'email' ? 'email' : 'SMS'}`;
    case 'snoozed': return c.state === 'not_snoozed' ? 'Not snoozed' : 'Snoozed';
    case 'archived': return c.state === 'active' ? 'Not archived' : 'Archived';
    case 'condition':
      return `Condition: ${c.tag || c.name || '…'}${c.require_verified ? ' (verified)' : ''}`;
    case 'medication':
      return `Medication: ${c.tag || c.name || '…'}${c.require_verified ? ' (verified)' : ''}`;
    case 'lab': {
      const what = c.tag_label || c.tag || c.test_name || '…';
      return `Lab ${what}${c.op ? ` ${num()}` : ''}${c.within_days ? ` (last ${c.within_days}d)` : ''}`;
    }
    case 'vital': {
      const label = VITAL_PRESET_OPTIONS.find((p) => p.value === c.preset)?.label || c.preset;
      return `${label}${c.op ? ` ${num()}` : ''}${c.within_days ? ` (last ${c.within_days}d)` : ''}`;
    }
    case 'demographic':
      return c.key === 'sex'
        ? `Sex: ${list(c.values, (v) => v)}`
        : `Age ${num()}`;
    case 'study_eligibility':
      return `${c.study_id ? studyByIdLabel(c.study_id) : '…'} eligibility: ${list(c.verdicts, (v) => STUDY_ELIGIBILITY_VERDICT_OPTIONS.find((o) => o.value === v)?.label || v)}`;
    case 'ai_review':
      return `AI review${c.study_id ? ` (${studyByIdLabel(c.study_id)})` : ''}: ${list(c.results, (v) => AI_REVIEW_RESULT_OPTIONS.find((o) => o.value === v)?.label || v)}`;
    case 'dq':
      return `Disqualified${(c.categories || []).length ? `: ${list(c.categories, (v) => DQ_CATEGORY_OPTIONS.find((o) => o.value === v)?.label || v)}` : ' (any reason)'}`;
    case 'communication': {
      const ch = list(c.channels, (v) => COMM_CHANNEL_OPTIONS.find((o) => o.value === v)?.label?.toLowerCase() || v);
      const dir = c.direction === 'inbound' ? 'inbound ' : c.direction === 'outbound' ? 'outbound ' : '';
      return `≥${c.min_count ?? 1} ${dir}${ch} in ${c.within_days ?? '…'}d`;
    }
    case 'form_submitted':
      return `Submitted: ${c.form_id ? formLabel(c.form_id) : '…'}${c.status === 'any' ? ' (incl. partial)' : ''}`;
    case 'answer': {
      const q = (c as any).label || c.question || '…';
      return `Answer ${q} ${c.op === 'answered' ? 'answered' : `${OP_LABELS[c.op || ''] || c.op} ${(c as any).valueLabel || (c.value ?? '…')}`}${c.form_id ? ` (on ${formLabel(c.form_id)})` : ''}`;
    }
    case 'answered': return `Answered: ${c.question || '…'}`;
    case 'has_records': return `Has ${(c.doc_types || ['medical_record']).map(prettySlug).join(' / ')} on file`;
    default: return c.degraded ? `${c.kind} (retired criterion)` : c.kind;
  }
}

/** Step summary for the funnel rail: custom label, or first condition + count. */
export function describeStep(step: SearchStepDef, ctx: DescribeCtx = {}): string {
  if (step.label) return step.label;
  if (!step.conditions.length) return 'Empty step';
  const first = describeCondition(step.conditions[0], ctx);
  const more = step.conditions.length - 1;
  const joiner = step.match === 'all' ? 'AND' : 'OR';
  return `${step.mode === 'exclude' ? 'NOT ' : ''}${first}${more > 0 ? ` ${joiner} ${more} more` : ''}`;
}

export const newStepId = (): string => `s_${Math.random().toString(36).slice(2, 10)}`;

export const emptyDefinition = (): SearchDefinition => ({
  version: 1,
  base: { exclude_archived: true, exclude_test: true, created_from: null, created_to: null },
  steps: [],
});

/** A condition is complete enough to evaluate (mirror of catalog validators —
 *  approximate on purpose; the server 400s with specifics on real errors). */
export function isConditionReady(c: SearchCondition): boolean {
  switch (c.kind) {
    case 'stage': return (c.statuses || []).length > 0;
    case 'stage_ever':
      return (c.statuses || []).length > 0
        || (c.min_ordinal !== undefined && c.min_ordinal !== null && String(c.min_ordinal) !== '');
    case 'study': return (c.slugs || []).length > 0;
    case 'utm': return Boolean(c.field) && (c.values || []).length > 0;
    case 'disposition': return true; // empty = "ever dispositioned" is valid
    case 'demographic': {
      if (c.key === 'sex') return (c.values || []).length > 0;
      if (!c.op || String(c.value ?? '').trim() === '' || !Number.isFinite(Number(c.value))) return false;
      if (c.op === 'between') return String(c.value2 ?? '').trim() !== '' && Number.isFinite(Number(c.value2));
      return true;
    }
    case 'study_eligibility': return Boolean(c.study_id) && (c.verdicts || []).length > 0;
    case 'ai_review': return (c.results || []).length > 0;
    case 'dq': return true; // empty = "ever disqualified" is valid
    case 'communication': return (c.channels || []).length > 0 && Number(c.within_days) >= 1;
    case 'owner': return (c.user_ids || []).length > 0 || c.include_unassigned === true;
    case 'incoming_eligibility': return (c.values || []).length > 0;
    case 'milestone': return (c.keys || []).length > 0;
    case 'lead_source': return (c.sources || []).length > 0;
    case 'tag': return (c.tags || []).length > 0;
    case 'interest': return (c.lists || []).length > 0;
    case 'created': return Boolean(c.from || c.to);
    case 'activity': return Number(c.days) >= 1;
    case 'condition':
    case 'medication': return Boolean((c.tag || '').trim() || (c.name || '').trim());
    case 'lab': {
      if (!(c.tag || '').trim() && !(c.test_name || '').trim()) return false;
      if (!c.op) return true;
      if (String(c.value ?? '').trim() === '' || !Number.isFinite(Number(c.value))) return false;
      if (c.op === 'between') return String(c.value2 ?? '').trim() !== '' && Number.isFinite(Number(c.value2));
      return true;
    }
    case 'vital': {
      if (!c.preset) return false;
      if (!c.op) return true;
      if (String(c.value ?? '').trim() === '' || !Number.isFinite(Number(c.value))) return false;
      if (c.op === 'between') return String(c.value2 ?? '').trim() !== '' && Number.isFinite(Number(c.value2));
      return true;
    }
    case 'form_submitted': return Boolean(c.form_id);
    case 'answer':
      return Boolean((c.question || '').trim()) && (c.op === 'answered' || String(c.value ?? '').trim() !== '');
    case 'answered': return Boolean((c.question || '').trim());
    default: return true; // no_study / contactable / snoozed / archived / has_records
  }
}

/** Strip client-only display fields before sending a definition to the API. */
export function wireDefinition(def: SearchDefinition): SearchDefinition {
  return {
    version: 1,
    base: { ...def.base },
    steps: def.steps
      .map((s) => ({
        ...s,
        conditions: s.conditions
          .filter(isConditionReady)
          .map((c) => {
            const { degraded, ...rest } = c as any;
            delete rest.label;
            delete rest.valueLabel;
            return rest as SearchCondition;
          }),
      }))
      .filter((s) => s.conditions.length > 0),
  };
}
